import { useState } from "react";
import { Controller } from "react-hook-form";
import { Button } from "@/components/button";
import { Modal } from "@/components/modal";
import { Toggle } from "@/components/toggle";
import { Form, FormSubmit } from "@/src/forms";
import {
  ACCOUNT_TYPE_DESCRIPTIONS,
  ACCOUNT_TYPE_LABELS,
  getAssignableRoles,
  type AccountType,
} from "../_data";
import { editMemberSchema, type EditMemberValues } from "../_schemas/edit-member";
import { updateUser, type UserAccount } from "@/src/services/company_service";

type Props = {
  open: boolean;
  onClose: () => void;
  member: UserAccount | null;
  /** Fired after the backend update succeeds; the parent reloads the list. */
  onSaved: () => void;
  onRemove: (uuid: string) => void;
  /** The editor's own role — actions are descending-only (e.g. an Admin can't promote someone to Admin). */
  actorRole: AccountType;
};

export function EditMemberModal({ open, onClose, member, onSaved, onRemove, actorRole }: Props) {
  const [saveError, setSaveError] = useState<string | null>(null);
  if (!member) return null;
  const current = member;
  const assignableRoles = getAssignableRoles(actorRole, current.accountType as AccountType);

  function handleClose() {
    setSaveError(null);
    onClose();
  }

  async function handleValid(values: EditMemberValues) {
    setSaveError(null);
    const res = await updateUser(current.uuid, {
      accountType: values.role,
      enableMonitoring: values.monitoring,
    });
    if (!res.ok) {
      setSaveError(res.error.message);
      return;
    }
    onSaved();
    handleClose();
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${current.firstName} ${current.lastName}`.trim() || current.email}
      description={current.email}
    >
      <Form
        // Remount on member change so defaultValues re-read from the new member.
        key={current.uuid}
        schema={editMemberSchema}
        defaultValues={{ role: current.accountType, monitoring: current.enableMonitoring }}
        onSubmit={handleValid}
        className="flex flex-col gap-4"
      >
        {saveError && (
          <p
            role="alert"
            className="rounded-[4px] border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
          >
            {saveError}
          </p>
        )}
        <Controller
          name="role"
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <span className="text-small font-medium text-grey-900">Role</span>
              {/* Vertical role list instead of a 5-wide <Tabs stretch> strip: the
                  five mixed-width labels (Owner … Auditor) clipped/misaligned in
                  the fixed-width modal. A list shows each role with its description
                  and reflows cleanly on narrow viewports. */}
              <div role="radiogroup" aria-label="Role" className="flex flex-col gap-2">
                {assignableRoles.map((t) => {
                  const selected = field.value === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => field.onChange(t)}
                      className={`flex items-start gap-3 rounded-[4px] border px-3 py-2 text-left transition-colors ${
                        selected
                          ? "border-grey-900 bg-grey-50"
                          : "border-grey-200 bg-white hover:bg-grey-50"
                      }`}
                    >
                      <span
                        aria-hidden
                        className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border ${
                          selected ? "border-grey-900" : "border-grey-200"
                        }`}
                      >
                        {selected && <span className="size-2 rounded-full bg-grey-900" />}
                      </span>
                      <span className="flex flex-col">
                        <span className="text-small font-medium text-grey-900">
                          {ACCOUNT_TYPE_LABELS[t]}
                        </span>
                        <span className="text-tiny text-grey-600">
                          {ACCOUNT_TYPE_DESCRIPTIONS[t]}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        />
        <Controller
          name="monitoring"
          render={({ field }) => (
            <ToggleRow
              label="Monitoring access"
              description="Receive alerts when monitored names change. Requires an active subscription."
              checked={!!field.value}
              onChange={field.onChange}
            />
          )}
        />

        <div className="flex items-center justify-between gap-2">
          <Button
            hierarchy="link"
            type="button"
            onClick={() => {
              onRemove(current.uuid);
              handleClose();
            }}
            className="text-red-600 hover:text-red-500 shrink-0"
          >
            Remove user
          </Button>
          <div className="flex gap-2">
            <Button hierarchy="secondary" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <FormSubmit hierarchy="primary" pendingLabel="Saving…">
              Save changes
            </FormSubmit>
          </div>
        </div>
      </Form>
    </Modal>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-[4px] bg-grey-50 px-3 py-2">
      <div className="flex flex-col">
        <span className="text-small font-medium text-black">{label}</span>
        <span className="text-tiny text-grey-900">{description}</span>
      </div>
      <Toggle checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </div>
  );
}
